import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import classNames from 'classnames';
import DataLayerPush from 'components/DataLayerPush';
import { sendMotorwayClickEvent } from 'utils/ga/events';
import { inputChange } from 'ducks/valuation';

import { isBelowLgViewPort } from 'utils/viewports';
import CarAttributes from '../CarAttributes';
import UsefulTools from '../UsefulTools/UsefulTools';
import style from './style.scss';

const CONDITIONS = ['poor', 'average', 'excellent'];
export class Valuation extends React.Component {
  static propTypes = {
    carDetails: PropTypes.shape({
      colour: PropTypes.string,
      derivative: PropTypes.string,
      manufacturer: PropTypes.string,
      model: PropTypes.string,
      year: PropTypes.string
    }),
    formValues: PropTypes.shape({
      condition: PropTypes.string,
      intent: PropTypes.string.isRequired,
      mileage: PropTypes.string.isRequired,
      vrm: PropTypes.string.isRequired
    }),
    inputChange: PropTypes.func,
    valuation: PropTypes.shape({
      priv: PropTypes.shape({
        average: PropTypes.string,
        excellent: PropTypes.string,
        poor: PropTypes.string
      }),
      px: PropTypes.shape({
        average: PropTypes.string,
        excellent: PropTypes.string,
        poor: PropTypes.string
      }),
      trade: PropTypes.shape({
        average: PropTypes.string,
        excellent: PropTypes.string,
        poor: PropTypes.string
      })
    }).isRequired
  };

  state = {
    iframeMouseOver: false
  };

  componentDidMount() {
    const frame = document.querySelector('iframe');
    if (frame.isConnected) {
      frame.addEventListener('mouseover', () => {
        this.setState({ iframeMouseOver: true });
      });
      frame.addEventListener('mouseout', () => {
        this.setState({ iframeMouseOver: false });
      });
    }
    window.addEventListener('blur', () => {
      if (this.state.iframeMouseOver) {
        sendMotorwayClickEvent();
      }
    });
  }

  componentWillUnmount() {
    const frame = document.querySelector('iframe');
    if (frame.isConnected) {
      frame.removeEventListener('mouseover', () => {
        this.setState({ iframeMouseOver: true });
      });
      frame.removeEventListener('mouseout', () => {
        this.setState({ iframeMouseOver: false });
      });
    }
    window.removeEventListener('blur', () => {
      if (this.state.iframeMouseOver) {
        sendMotorwayClickEvent();
      }
    });
  }

  handleChange = value => this.props.inputChange('condition', value);

  render() {
    const {
      carDetails: { colour, derivative, manufacturer, model, year },
      formValues: { condition, intent, mileage, vrm },
      valuation: { priv, px, trade }
    } = this.props;
    const car = `${manufacturer} ${model} ${derivative}`;
    const conditionText = {
      average: 'Minor but obvious body or mechanical issues',
      excellent: 'No bodywork or mechanical faults',
      poor: 'Obvious bodywork or mechanical faults'
    };
    const dataLayerData = {
      content_type: 'valuations',
      navigation_section: 'step 3',
      make: manufacturer,
      model: model
    };

    const widgetHeight = isBelowLgViewPort() ? '450' : '300';

    let utmCampaign;
    switch (intent) {
      case 'buying':
        utmCampaign = 'buyingthiscar';
        break;
      case 'insuring':
        utmCampaign = 'insuringthiscar';
        break;
      case 'just-interested':
        utmCampaign = 'justinterested';
        break;
    }

    return mileage ? (
      <div className={style.row}>
        <div className={classNames(style.col, style.formContainer)}>
          <h3 className={style.stepHeading}>Value your car</h3>
          <div className={style.stepImage}>
            <div className={style.blueLine} />
            <div className={style.steps}>
              <div className={style.stepDone} />
              <div className={style.stepDone} />
              <div data-cy="step3" className={style.stepActive}>
                3
              </div>
            </div>
          </div>
          <div className={style.carDetails}>
            <CarAttributes
              car={car}
              colour={colour}
              year={year}
              vrm={vrm}
              mileage={mileage}
            />
            <h5 className={style.carCondition}>Select vehicle condition</h5>
            <div className={style.conditionToggle}>
              {CONDITIONS.map(carCondition => (
                <div
                  key={carCondition}
                  className={classNames(style.toggleBoxContainer, {
                    [style.toggleBoxContainerSelected]:
                      condition === carCondition
                  })}
                  onClick={() => this.handleChange(carCondition)}
                >
                  <input
                    checked={condition === carCondition}
                    id={carCondition}
                    name="condition"
                    type="radio"
                    onChange={() => this.handleChange(carCondition)}
                    value={carCondition}
                  />
                  <div className={style.label}>
                    <p className={style.labelTitle}>{carCondition}</p>
                    <p className={style.labelText}>
                      {conditionText[carCondition]}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {(intent === 'buying' ||
              intent === 'just-interested' ||
              intent === 'insuring') && (
              <div className={style.prosAndCons}>
                <div>
                  <div className={style.prosConsHeader}>
                    <h2>Buying privately</h2>
                    <div data-cy="privateprice" className={style.price}>
                      &pound;
                      {parseInt(priv[condition]).toLocaleString()}
                    </div>
                  </div>
                  <div className={style.container}>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        You can ask the owner questions directly
                      </p>
                    </div>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        The owner should know their car&apos;s history
                      </p>
                    </div>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        Private sale prices are often lower than at a dealership
                      </p>
                    </div>
                  </div>
                  <div className={style.container}>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        You have very few rights if the car goes wrong
                      </p>
                    </div>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        You must arrange insurance cover to test drive the car
                      </p>
                    </div>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        You won&apos;t recieve guidance when arranging finance
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className={style.prosConsHeader}>
                    <h2>Buying from a dealer</h2>
                    <div data-cy="tradeprice" className={style.price}>
                      &pound;
                      {parseInt(trade[condition]).toLocaleString()}
                    </div>
                  </div>
                  <div className={style.container}>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        Sales team will be familiar with arranging finance
                      </p>
                    </div>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        Dealer may help if you have problems, in order to
                        maintain a strong reputation
                      </p>
                    </div>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        Opportunity to negotiate for discounted aftercare, eg
                        MOTs or servicing
                      </p>
                    </div>
                  </div>
                  <div className={style.container}>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        Typically more expensive purchase price
                      </p>
                    </div>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        Dealer might try to push products to earn maximum
                        commission
                      </p>
                    </div>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        Professional car dealers are typically expert
                        negotiators
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {(intent === 'selling-buying-new' ||
              intent === 'selling-buying-used') && (
              <div className={style.prosAndCons}>
                <div>
                  <div className={style.prosConsHeader}>
                    <h2>Selling privately</h2>
                    <div data-cy="privateprice" className={style.price}>
                      &pound;
                      {parseInt(priv[condition]).toLocaleString()}
                    </div>
                  </div>
                  <div className={style.container}>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        You are in control of the sale
                      </p>
                    </div>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        The sale price is usually more than a part-exchange
                        value
                      </p>
                    </div>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        You can plan your next car purchase with cash in hand
                      </p>
                    </div>
                  </div>
                  <div className={style.container}>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        Dealing with buyers is time consuming
                      </p>
                    </div>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        Cost of advertising on sales sites
                      </p>
                    </div>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        Uncertainty of how long it will take to sell your car
                      </p>
                    </div>
                  </div>
                </div>
                <div className={style.px}>
                  <div className={style.prosConsHeader}>
                    <h2>Part-exchange</h2>
                    <div data-cy="partexprice" className={style.price}>
                      &pound;
                      {parseInt(px[condition]).toLocaleString()}
                    </div>
                  </div>
                  <div className={style.container}>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        Forms part of an established, professional sales process
                      </p>
                    </div>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        Typically less time consuming than selling privately
                      </p>
                    </div>
                    <div className={style.pro}>
                      <div className={style.proCheck} />
                      <p className={style.proText}>
                        Sale can be used as leverage for new car purchase
                        discount
                      </p>
                    </div>
                  </div>
                  <div className={style.container}>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        Dealers usually pay slightly less than private buyers
                      </p>
                    </div>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        Professional car dealers are typically expert
                        negotiators
                      </p>
                    </div>
                    <div className={style.con}>
                      <div className={style.conCheck} />
                      <p className={style.conText}>
                        The value of certain optional extras might not be
                        considered
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={style.bottomContainer}>
            <div>
              {(intent === 'buying' ||
                intent === 'just-interested' ||
                intent === 'insuring') && (
                <iframe
                  src={`https://motorway.s3.eu-west-2.amazonaws.com/affiliate-widgets/widget-2020-min.html?utm_source=whatcar&utm_medium=affiliate&utm_campaign=${utmCampaign}`}
                  title="Motorway"
                  width="100%"
                  height={widgetHeight}
                  style={{ border: 'none', display: 'block' }}
                />
              )}
              {(intent === 'selling-buying-new' ||
                intent === 'selling-buying-used') && (
                <iframe
                  src="https://motorway.s3.eu-west-2.amazonaws.com/affiliate-widgets/widget-2020-min.html?utm_source=whatcar&utm_medium=affiliate&utm_campaign=valuationstool"
                  title="Motorway"
                  width="100%"
                  height={widgetHeight}
                  style={{ border: 'none', display: 'block' }}
                />
              )}
            </div>
            <UsefulTools />
            <p className={style.partners}>
              What Car? Valuations data partners: Auto Trader
            </p>
          </div>
        </div>
        {dataLayerData && (
          <DataLayerPush event={'view_page'} {...dataLayerData} />
        )}
      </div>
    ) : (
      <Redirect to="/car-valuation/" />
    );
  }
}

const mapStateToProps = state => ({
  carDetails: {
    colour: state.valuation.carDetails.colour,
    derivative: state.valuation.carDetails.derivative,
    manufacturer: state.valuation.carDetails.manufacturer,
    model: state.valuation.carDetails.model,
    year: state.valuation.carDetails.year
  },
  formValues: {
    condition: state.valuation.formValues.condition,
    intent: state.valuation.formValues.intent,
    mileage: state.valuation.formValues.mileage,
    vrm: state.valuation.formValues.vrm
  },
  valuation: {
    priv: state.valuation.valuation.priv,
    px: state.valuation.valuation.px,
    trade: state.valuation.valuation.trade
  }
});

const mapDispatchToProps = {
  inputChange
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Valuation);



// WEBPACK FOOTER //
// ./src/components/Valuation/index.js